/**
 * Скелетони у формі майбутнього вмісту (UX-32; states.md, розд. 2). Розміри — з токенів щільності
 * (`.table` бере висоту рядка `--cl-row-h`), тож заміна на дані не зсуває вміст (CLS).
 * Скелетон декоративний: `aria-hidden`, без фокусу; оголошення «Завантаження…» робить `Pending`.
 * Модуль — у першому екрані (список, очікування маршрутів), тож тут лише те, що бере він; скелетони
 * лише лінивих чанків — в окремих файлах `ui/Skeleton*.tsx` (performance-budget.md, розд. 3).
 */
import type { ReactNode } from 'react';

/** Таблиця: шапка екрана вже на місці, рядки — смужки. */
export function SkeletonTable({ label, rows }: { label: string; rows: number }) {
  return (
    <table className="table" aria-label={label} aria-hidden="true" data-testid="list-skeleton">
      <tbody className="sk-group">
        {Array.from({ length: rows }, (_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: статичний скелетон без даних
          <tr key={i}>
            <td>
              <span className="sk sk--line" />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Рядки стрічки чи списку подій: `rows` смужок одна під одною. */
export function SkeletonLines({ rows }: { rows: number }) {
  return (
    <div className="sk-group" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: статичний скелетон без даних
        <span key={i} className="sk sk--line" />
      ))}
    </div>
  );
}

/**
 * Картка запису на всю робочу область: шапка і три колонки (REC-01; маршрут `/r`, states.md, розд. 2).
 * `title` — справжня назва запису, коли вона вже відома з кешу (`CardPending`): стоїть на місці
 * смужки заголовка, без зсуву.
 */
export function SkeletonCard({ title }: { title?: ReactNode } = {}) {
  return (
    <div className="record sk-group" aria-busy="true" data-testid="card-skeleton">
      {/* Ті самі два рядки, що й у шапці (32 і 28 px, layout.md, розд. 7, правило 6): без зсуву. */}
      <div className="record__head">
        <div className="record__titlerow">
          {title ?? <span className="sk sk--title record__sk-title" aria-hidden="true" />}
        </div>
        <div className="record__stagerow record__sk-stage" aria-hidden="true">
          <span className="sk sk--line" />
        </div>
      </div>
      <div className="record__cols" aria-hidden="true">
        {(['record__left', 'record__center', 'record__right'] as const).map((col) => (
          <div key={col} className={col}>
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} className="sk sk--line" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Стовпці дошки до етапів (`BoardPending`): `columns` смуг, по одній смужці. */
export function SkeletonBoard({ columns }: { columns: number }) {
  return (
    <div className="kb" aria-hidden="true">
      {Array.from({ length: columns }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: статичний скелетон без даних
        <div key={i} className="kb__col">
          <div className="kb__list sk-group">
            <span className="sk sk--line" />
          </div>
        </div>
      ))}
    </div>
  );
}
